import React from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { UtilisationTrend } from '@/types/utilisation';

interface WeeklyBreakdownSectionProps {
  trends: UtilisationTrend[];
}

export const WeeklyBreakdownSection: React.FC<WeeklyBreakdownSectionProps> = ({
  trends,
}) => {
  if (trends.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Weekly Breakdown</CardTitle>
        <CardDescription>Utilisation metrics by week</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px]">
          <div className="space-y-2">
            {trends.map((trend, index) => (
              <div
                key={index}
                className={cn(
                  "p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer",
                  index !== trends.length - 1 && "border-b"
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium">
                      {format(trend.weekEnding, 'MMM d, yyyy')}
                    </span>
                    <Badge variant="outline" className="ml-2">
                      {trend.employeeCount} {trend.employeeCount === 1 ? 'employee' : 'employees'}
                    </Badge>
                  </div>
                  <span className={cn(
                    "text-sm font-semibold",
                    trend.utilisationRate >= 0.75
                      ? 'text-success-500'
                      : trend.utilisationRate >= 0.5
                        ? 'text-warning-500'
                        : 'text-error-500'
                  )}>
                    {(trend.utilisationRate * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden mb-2">
                  <div
                    className={cn(
                      'h-full transition-all',
                      trend.utilisationRate >= 0.75
                        ? 'bg-success-500'
                        : trend.utilisationRate >= 0.5
                          ? 'bg-warning-500'
                          : 'bg-error-500'
                    )}
                    style={{ width: `${trend.utilisationRate * 100}%` }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Total:</span>
                    <span className="ml-1 font-medium">{trend.totalHours.toFixed(1)}h</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Billable:</span>
                    <span className="ml-1 font-medium">{trend.billableHours.toFixed(1)}h</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Non-billable:</span>
                    <span className="ml-1 font-medium">{(trend.totalHours - trend.billableHours).toFixed(1)}h</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
